// Блок «Резервное копирование базы» на экране метрик (Б-1): подписи, цвета и
// строки-пояснения из ответа `backups` /agents/statut/metrics. Только чистые
// функции и `import type` — их проверяет node:test (backupHealth.test.mjs).
import type { BackupHealth, BackupKindSummary } from './types';

export type BackupTone = 'ok' | 'warn' | 'danger' | 'neutral';
export type BackupKind = 'dump' | 'offsite' | 'restore_test';

/** Коды проблем бэкенда начинаются с вида: dump_*, offsite_*, restore_*. */
const PROBLEM_PREFIX: Record<BackupKind, string> = {
  dump: 'dump_',
  offsite: 'offsite_',
  restore_test: 'restore_',
};

export function backupStateLabel(state: BackupHealth['state']): string {
  switch (state) {
    case 'ok':
      return 'В порядке';
    case 'failed':
      return 'Сбой';
    case 'not_configured':
      return 'Не включено';
    default:
      return 'Нет данных';
  }
}

/** Невключённое копирование — тоже красное: копий нет. */
export function backupStateTone(state: BackupHealth['state']): BackupTone {
  if (state === 'ok') return 'ok';
  if (state === 'unknown') return 'warn';
  return 'danger';
}

/** Давность по часам: «меньше часа», «5 ч», «3 сут»; null — «ни разу». */
export function backupAgo(hours: number | null | undefined): string {
  if (hours == null) return 'ни разу';
  if (hours < 1) return 'меньше часа назад';
  if (hours < 48) return `${Math.round(hours)} ч назад`;
  return `${Math.floor(hours / 24)} сут назад`;
}

/** Цвет карточки вида: красный, если бэкенд назвал проблему этого вида. */
export function backupKindTone(health: BackupHealth, kind: BackupKind): BackupTone {
  if (health.state === 'not_configured' || health.state === 'unknown') return 'neutral';
  const prefix = PROBLEM_PREFIX[kind];
  if (health.problems.some((p) => p.code.startsWith(prefix))) return 'danger';
  return health[kind]?.last_ok_at ? 'ok' : 'neutral';
}

function num(details: Record<string, unknown> | null | undefined, key: string): number | null {
  const v = details?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function mib(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} ГиБ`;
  return `${Math.round(bytes / 1024 ** 2)} МиБ`;
}

function joined(parts: Array<string | null>): string {
  return parts.filter((p): p is string => Boolean(p)).join(' · ');
}

/** «комплект …, дамп 4.8 ГиБ, записей 24 (новых 1)». */
export function backupDumpSub(s: BackupKindSummary | undefined): string {
  if (!s?.last_ok_at) return 'удачных копий нет';
  const d = s.last_ok_details;
  const total = num(d, 'recordings_total');
  const fresh = num(d, 'recordings_new');
  return joined([
    s.last_ok_set ? `комплект ${s.last_ok_set}` : null,
    mib(num(d, 'main_bytes')) ? `дамп ${mib(num(d, 'main_bytes'))}` : null,
    total != null ? `записей ${total}${fresh != null ? ` (новых ${fresh})` : ''}` : null,
  ]);
}

/** «S3» или «компьютер администратора · копий 19». */
export function backupOffsiteSub(
  s: BackupKindSummary | undefined,
  mode: BackupHealth['offsite_mode'],
): string {
  const where = mode === 's3' ? 'S3' : 'компьютер администратора';
  const copies = num(s?.last_ok_details, 'local_sets');
  return joined([where, copies != null && mode !== 's3' ? `копий ${copies}` : null]);
}

/** «таблиц 402, записей 24 · позиций объёмов 21276». */
export function backupRestoreSub(s: BackupKindSummary | undefined): string {
  if (!s?.last_ok_at) return 'восстановление не проверялось';
  const d = s.last_ok_details;
  const tables = num(d, 'tables_checked');
  const recs = num(d, 'recordings_checked');
  const keys = d?.key_counts as Record<string, unknown> | undefined;
  const positions = keys?.['public.pto_volume_positions'];
  return joined([
    tables != null ? `таблиц ${tables}` : null,
    recs != null ? `записей ${recs}` : null,
    typeof positions === 'number' ? `позиций объёмов ${positions}` : null,
  ]);
}
